import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FixedTab, RouteTabs, VariableTab } from '../../../components/RouteTabs';
import useAlertDialog from '../../../hooks/useAlertDialog';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { deleteUserSegment, getUserSegmentsWithTypes, getUserSegmentsWithTypesAndPage, toggleActiveUserSegment } from '../../../services/api/Segment';
import {
  IUserSegment,
  IUserSegmentAutoUpdate,
  IUserSegmentGroup,
  IUserSegmentSystem,
  IUserSegmentTrigger,
  IUserSegmentWebhook,
  IUserSegmentProduct,
  UserSegmentType,
} from '../../../services/types';
import { RouteNames } from '../../config';
import { AbtestScheduleList } from './AbtestScheduleList';
import { AllList } from './AllList';
import { AutoUpdateList } from './AutoUpdateList';
import { CurationScheduleList } from './CurationScheduleList';
import { GroupList } from './GroupList';
import Preview from './Preview';
import { TriggerList } from './TriggerList';
import CreateUserSegment from './ViewAndCreateUserSegment';
import { WebhookList } from './WebhookList';
import { ProductList } from './ProductList';

interface IProps {}

export type IUserSegmentAll =
  | IUserSegmentAutoUpdate
  | IUserSegmentGroup
  | IUserSegmentTrigger
  | IUserSegmentWebhook
  | IUserSegmentSystem
  | IUserSegmentProduct;

interface IParamProps {
  segmentId: string;
}

const UserSegment = (props: IProps & RouteComponentProps): JSX.Element => {
  const path = RouteNames.UserSegment;

  // uses
  const match = useRouteMatch<IParamProps>(`${RouteNames.UserSegment}/v/:segmentId`);
  const history = useHistory();
  const location = useLocation();
  const snackbar = useSnackbar();
  const segmentId = match?.params?.segmentId;
  const initialSegmentIds = segmentId ? [Number(segmentId)] : [];
  const backdrop = useBackdrop();
  const alert = useAlertDialog();
  const confirm = useConfirmDialog();

  // states
  const [editingSegmentIds, setEditingSegmentIds] = useState<number[]>(initialSegmentIds);
  const [userSegmentAll, setUserSegmentAll] = useState<IUserSegmentAll[]>();
  const isUserSegmentAllLoaded = useRef<boolean>(false);
  const [userSegmentGroups, setUserSegmentGroups] = useState<IUserSegmentGroup[]>();
  const [userSegmentAutoUpdates, setUserSegmentAutoUpdates] = useState<IUserSegmentAutoUpdate[]>();
  const [userSegmentTriggers, setUserSegmentTriggers] = useState<IUserSegmentTrigger[]>();
  const [userSegmentWebhooks, setUserSegmentWebhooks] = useState<IUserSegmentWebhook[]>();
  const [userSegmentProducts, setUserSegmentProducts] = useState<IUserSegmentProduct[]>();
  // all list only
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);

  const getMergedSegments = useCallback(() => [...(userSegmentAutoUpdates || []), ...(userSegmentTriggers || []), ...(userSegmentWebhooks || [])], [
    userSegmentAutoUpdates,
    userSegmentTriggers,
    userSegmentWebhooks,
  ]);

  // refs
  const allTypeCanceler = useRef<Canceler>(() => undefined);
  const groupTypeCanceler = useRef<Canceler>(() => undefined);
  const autoupdateTypeCanceler = useRef<Canceler>(() => undefined);
  const triggerTypeCanceler = useRef<Canceler>(() => undefined);
  const webhookTypeCanceler = useRef<Canceler>(() => undefined);
  const productTypeCanceler = useRef<Canceler>(() => undefined);

  // APIs
  const fetchAllTypeData = useCallback(async () => {
    if (isUserSegmentAllLoaded.current) {
      backdrop.show();
    }

    allTypeCanceler.current();

    const getFilteredUserSegments = async (items: IUserSegmentAll[]): Promise<IUserSegmentAll[]> => {
      const filteredList = items.filter(item => item.type !== 'system' && item.id !== 45);

      if (page === 0 && isActive) {
        const [fetchPromiseTrigger] = getUserSegmentsWithTypes<IUserSegmentTrigger>([UserSegmentType.Trigger]);
        const triggerResult = await fetchPromiseTrigger;
        filteredList.unshift(...triggerResult.data.items.filter(item => item.id === 45));

        const [fetchPromiseSystem] = getUserSegmentsWithTypes<IUserSegmentSystem>([UserSegmentType.System]);
        const systemResult = await fetchPromiseSystem;
        filteredList.unshift(...systemResult.data.items.filter(item => item.name.toLowerCase() === 'all user'));
      }

      return filteredList;
    };

    try {
      const [fetchPromise, cancel] = getUserSegmentsWithTypesAndPage<IUserSegmentAll>(
        [UserSegmentType.AutoUpdate, UserSegmentType.Group, UserSegmentType.Trigger, UserSegmentType.Webhook, UserSegmentType.Product],
        isActive,
        page + 1,
        rowsPerPage,
      );
      allTypeCanceler.current = cancel;

      const result = await fetchPromise;
      const userSegmentAll = await getFilteredUserSegments(result.data.items);

      isUserSegmentAllLoaded.current = true;

      setUserSegmentAll(userSegmentAll);
      setTotalCount(result.data.totalCount || 0);
    } catch (e) {
      if (api.isCancel(e)) return;
    } finally {
      backdrop.hide();
    }
  }, [backdrop, isActive, page, rowsPerPage]);

  const fetchGroupTypeData = async () => {
    groupTypeCanceler.current();

    try {
      const [fetchPromise, cancel] = getUserSegmentsWithTypes<IUserSegmentGroup>([UserSegmentType.Group]);
      groupTypeCanceler.current = cancel;

      const result = await fetchPromise;
      setUserSegmentGroups(result.data.items);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const fetchAutoUpdateTypeData = async () => {
    autoupdateTypeCanceler.current();

    try {
      const [fetchPromise, cancel] = getUserSegmentsWithTypes<IUserSegmentAutoUpdate>([UserSegmentType.AutoUpdate]);
      autoupdateTypeCanceler.current = cancel;

      const result = await fetchPromise;
      setUserSegmentAutoUpdates(result.data.items);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const fetchTriggerTypeData = async () => {
    triggerTypeCanceler.current();

    try {
      const [fetchPromise, cancel] = getUserSegmentsWithTypes<IUserSegmentTrigger>([UserSegmentType.Trigger]);
      triggerTypeCanceler.current = cancel;

      const result = await fetchPromise;
      setUserSegmentTriggers(result.data.items);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const fetchWebhookTypeData = async () => {
    webhookTypeCanceler.current();

    try {
      const [fetchPromise, cancel] = getUserSegmentsWithTypes<IUserSegmentWebhook>([UserSegmentType.Webhook]);
      webhookTypeCanceler.current = cancel;

      const result = await fetchPromise;
      setUserSegmentWebhooks(result.data.items);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const fetchProductTypeData = async () => {
    productTypeCanceler.current();

    try {
      const [fetchPromise, cancel] = getUserSegmentsWithTypes<IUserSegmentProduct>([UserSegmentType.Product]);
      productTypeCanceler.current = cancel;

      const result = await fetchPromise;
      setUserSegmentProducts(result.data.items);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  // handlers
  const handleRowItemClick = useCallback(
    (segmentItem: IUserSegment) => {
      if (!editingSegmentIds.find(id => id === segmentItem.id)) {
        setEditingSegmentIds([...editingSegmentIds, segmentItem.id!]);
      }
    },
    [editingSegmentIds],
  );

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.response ? e.response.data.message : e.message}`);
    },
    [snackbar],
  );

  const handleGroupListUpdate = useCallback((data: IUserSegmentGroup[]) => {
    setUserSegmentGroups(data);
  }, []);
  const handleAutoupdateListUpdate = useCallback((data: IUserSegmentAutoUpdate[]) => {
    setUserSegmentAutoUpdates(data);
  }, []);
  const handleTriggerListUpdate = useCallback((data: IUserSegmentTrigger[]) => {
    setUserSegmentTriggers(data);
  }, []);
  const handleWebhookListUpdate = useCallback((data: IUserSegmentWebhook[]) => {
    setUserSegmentWebhooks(data);
  }, []);
  const handleProductListUpdate = useCallback((data: IUserSegmentProduct[]) => {
    setUserSegmentProducts(data);
  }, []);
  const handleChangeIsActive = (active: boolean) => {
    setPage(0);
    setIsActive(active);
  };

  // watches
  useEffect(() => {
    if (location.pathname === RouteNames.UserSegment) {
      fetchAllTypeData();
    }
  }, [page, isActive, rowsPerPage, location.pathname, fetchAllTypeData]);

  useEffect(() => {
    const fetchData = async () => {
      switch (location.pathname) {
        case RouteNames.UserSegment:
          !userSegmentAll && fetchAllTypeData();
          break;
        case `${RouteNames.UserSegment}/autoupdate`:
          !userSegmentAutoUpdates && fetchAutoUpdateTypeData();
          break;
        case `${RouteNames.UserSegment}/group`:
          !userSegmentGroups && fetchGroupTypeData();
          break;
        case `${RouteNames.UserSegment}/trigger`:
          !userSegmentTriggers && fetchTriggerTypeData();
          break;
        case `${RouteNames.UserSegment}/webhook`:
          !userSegmentWebhooks && fetchWebhookTypeData();
          break;
        case `${RouteNames.UserSegment}/product`:
          !userSegmentProducts && fetchProductTypeData();
          break;
      }
    };

    fetchData();

    return () => {
      autoupdateTypeCanceler.current();
      triggerTypeCanceler.current();
      webhookTypeCanceler.current();
    };
  }, [location.pathname, userSegmentAutoUpdates, userSegmentTriggers, userSegmentWebhooks, userSegmentAll, userSegmentGroups, fetchAllTypeData]);

  const handleRowClick = useCallback(
    (segmentId: number) => {
      if (!editingSegmentIds.find(id => id === segmentId)) {
        setEditingSegmentIds([...editingSegmentIds, segmentId]);
      }
    },
    [editingSegmentIds],
  );
  const handleCreate = useCallback(
    data => {
      if (data.type === UserSegmentType.AutoUpdate) {
        if (userSegmentAutoUpdates !== undefined) {
          setUserSegmentAutoUpdates([data, ...userSegmentAutoUpdates]);
        }
      } else if (data.type === UserSegmentType.Trigger) {
        if (userSegmentTriggers !== undefined) {
          setUserSegmentTriggers([data, ...userSegmentTriggers]);
        }
      } else if (data.type === UserSegmentType.Webhook) {
        if (userSegmentWebhooks !== undefined) {
          setUserSegmentWebhooks([data, ...userSegmentWebhooks]);
        }
      } else if (data.type === UserSegmentType.Group) {
        if (userSegmentGroups !== undefined) {
          setUserSegmentGroups([data, ...userSegmentGroups]);
        }
      }
      setPage(0);
      setIsActive(true);
      fetchAllTypeData();
      history.push(`${RouteNames.UserSegment}`);

      snackbar.success('Saved successfully.');
    },
    [snackbar, history, userSegmentAutoUpdates, userSegmentTriggers, userSegmentWebhooks, userSegmentGroups],
  );
  const handleEdit = useCallback(() => {
    snackbar.success('Saved changes Successfully.');
  }, [snackbar, userSegmentAutoUpdates, userSegmentTriggers, userSegmentWebhooks]);
  const handleTabClose = useCallback(
    segmentId => {
      setEditingSegmentIds(editingSegmentIds.filter(id => id !== segmentId));
    },
    [editingSegmentIds],
  );
  const handleClickCuration = useCallback(
    (segmentItem: IUserSegment, mode: 'create' | 'edit') => {
      if (!editingSegmentIds.find(id => id === segmentItem.id)) {
        setEditingSegmentIds([...editingSegmentIds, segmentItem.id!]);
      }
    },
    [editingSegmentIds],
  );
  const handleOnToggleStatus = async (seg: IUserSegmentAll, type: UserSegmentType | 'all') => {
    if (!seg.id) {
      return;
    }
    confirm.open({
      title: 'Status change',
      text: seg.isActive
        ? 'This will deactivate the segment. Are you sure you want to change the status of the segment?'
        : 'Are you sure you want to activate this segment?',
      onProceed: async () => {
        try {
          backdrop.show();
          const result = await toggleActiveUserSegment(seg.id!.toString(), seg.type);
          if (result.status === 200) {
            switch (type) {
              case UserSegmentType.AutoUpdate:
                fetchAutoUpdateTypeData();
                break;
              case UserSegmentType.Group:
                fetchGroupTypeData();
                break;
              case UserSegmentType.Trigger:
                fetchTriggerTypeData();
                break;
              case UserSegmentType.Webhook:
                fetchWebhookTypeData();
                break;
              case 'all':
                fetchAllTypeData();
                break;
              default:
                alert.open({ title: 'Alert', text: 'no type ' });
                break;
            }
          } else {
            handleError(new Error(result.statusText));
          }
        } catch (e) {
          if (e.response.data.message.indexOf('parent') >= 0) {
            alert.open({ title: 'Alert', text: 'You cannot deactivate a segment that is in a group segment.' });
          } else {
            handleError(e);
          }
        } finally {
          backdrop.hide();
        }
      },
    });
  };
  const handleListDelete = async (seg: IUserSegmentAll, type: UserSegmentType | 'all', idx: number) => {
    if (seg.type !== UserSegmentType.Group && seg.isActive) {
      return;
    }

    const onProceed = async () => {
      if (!seg.id) {
        return;
      }

      try {
        backdrop.show();
        const result = await deleteUserSegment(seg.id);

        if (result.status === 200) {
          switch (type) {
            case UserSegmentType.AutoUpdate:
              fetchAutoUpdateTypeData();
              break;
            case UserSegmentType.Group:
              fetchGroupTypeData();
              break;
            case UserSegmentType.Trigger:
              fetchTriggerTypeData();
              break;
            case UserSegmentType.Webhook:
              fetchWebhookTypeData();
              break;
            case 'all':
              fetchAllTypeData();
              break;
            default:
              alert.open({ title: 'Alert', text: 'no type ' });
              break;
          }
          if (userSegmentAll !== undefined) {
            fetchAllTypeData();
          }
        } else {
          handleError(new Error(result.statusText));
        }
      } catch (e) {
        handleError(e);
      } finally {
        backdrop.hide();
      }
    };

    confirm.open({
      title: `Delete`,
      text: `Are you sure you want to delete this segment?`,
      onProceed,
    });
  };

  // funcs
  const getTabLabel = useCallback(segmentId => getMergedSegments().find(s => s.id === segmentId)?.name || `View ${segmentId}`, [getMergedSegments]);
  const getTabUri = useCallback((segmentId: number) => {
    return history.location.pathname.indexOf(`v/${segmentId}`) > 0 ? `${history.location.pathname.match(/(v\/\d+.*)$/)?.[1]}` : `v/${segmentId}`;
  }, []);
  const keyExtractor = useCallback(segmentId => `tab-floating-${segmentId}`, []);
  const variableTabRenderer = useCallback(segmentId => <CreateUserSegment segmentId={segmentId} onCreated={handleEdit} onError={handleError} />, [
    handleEdit,
    handleError,
  ]);

  return (
    <div>
      <RouteTabs path={path}>
        <FixedTab label="All Segments">
          <RouteTabs isNested={true} path={path}>
            <FixedTab label="All Segments">
              <AllList
                userSegmentAll={userSegmentAll}
                page={page}
                totalCount={totalCount}
                isActive={isActive}
                rowsPerPage={rowsPerPage}
                onClickRowItem={handleRowItemClick}
                onError={handleError}
                onClickCuration={handleClickCuration}
                setIsActive={handleChangeIsActive}
                fetchAllTypeData={fetchAllTypeData}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
                onToggleStatus={handleOnToggleStatus}
                onDelete={handleListDelete}
              />
            </FixedTab>
            <FixedTab label="Autoupdate" uri="autoupdate">
              <AutoUpdateList
                data={userSegmentAutoUpdates}
                onClickRow={handleRowClick}
                onError={handleError}
                onUpdate={handleAutoupdateListUpdate}
                onDelete={handleListDelete}
                onClickCuration={handleClickCuration}
                onToggleStatus={handleOnToggleStatus}
              />
            </FixedTab>
            <FixedTab label="Trigger" uri="trigger">
              <TriggerList
                data={userSegmentTriggers}
                onClickRow={handleRowClick}
                onError={handleError}
                onUpdate={handleTriggerListUpdate}
                onDelete={handleListDelete}
                onClickCuration={handleClickCuration}
                onToggleStatus={handleOnToggleStatus}
              />
            </FixedTab>
            <FixedTab label="Webhook" uri="webhook">
              <WebhookList
                data={userSegmentWebhooks}
                onClickRow={handleRowClick}
                onError={handleError}
                onUpdate={handleWebhookListUpdate}
                onDelete={handleListDelete}
                onClickCuration={handleClickCuration}
                onToggleStatus={handleOnToggleStatus}
              />
            </FixedTab>
            <FixedTab label="Group" uri="group">
              <GroupList
                data={userSegmentGroups}
                onClickRow={handleRowClick}
                onError={handleError}
                onUpdate={handleGroupListUpdate}
                onDelete={handleListDelete}
                onClickCuration={handleClickCuration}
                onToggleStatus={handleOnToggleStatus}
              />
            </FixedTab>
            <FixedTab label="Product" uri="product">
              <ProductList
                data={userSegmentProducts}
                onClickRow={handleRowClick}
                onError={handleError}
                onUpdate={handleProductListUpdate}
                onDelete={handleListDelete}
                onClickCuration={handleClickCuration}
                onToggleStatus={handleOnToggleStatus}
              />
            </FixedTab>
          </RouteTabs>
        </FixedTab>
        <FixedTab label="Create a Segment" uri="create">
          <CreateUserSegment onCreated={handleCreate} onError={handleError} />
        </FixedTab>
        <FixedTab label="Curation Schedule" uri="curation-schedules">
          <CurationScheduleList onClickRowItem={handleRowItemClick} onError={handleError} onClickCuration={handleClickCuration} />
        </FixedTab>
        <FixedTab label="A/B Test Schedule" uri="abtest-schedules">
          <AbtestScheduleList onClickRowItem={handleRowItemClick} onError={handleError} onClickCuration={handleClickCuration} />
        </FixedTab>
        <FixedTab label="Preview" uri="preview">
          <Preview />
        </FixedTab>
        <VariableTab<number>
          data={editingSegmentIds}
          label={getTabLabel}
          uri={getTabUri}
          onClose={handleTabClose}
          keyExtractor={keyExtractor}
          render={variableTabRenderer}
        />
      </RouteTabs>
    </div>
  );
};

export default UserSegment;
